import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  atualizarCanal,
  atualizarRegra,
  criarCanal,
  criarRegra,
  listarCanais,
  listarRegras,
  testarCanal,
} from "./api";
import type {
  Canal,
  CanalIn,
  CanalPatch,
  Regra,
  RegraIn,
  RegraPatch,
  TesteCanal,
} from "./tipos";

/*
 * Hooks de server-state do módulo de Canais (FE-6a — ADR 0026 §Estado / TanStack
 * Query). Toda leitura/escrita passa pelo Query: cache, dedup e revalidação com a
 * config conservadora do `criarQueryClient` (staleTime 30s, retry 1 em query;
 * `mutations:{retry:0}`).
 *
 * queryKey: `["config-alertas","canais"]` (lista). As mutações de criar/editar
 * invalidam essa key no `onSuccess` — a tabela revalida sozinha no próximo fetch.
 *
 * Mutations NÃO setam `retry`: `criarQueryClient` já aplica `mutations:{retry:0}`.
 * `useTestarCanal` em especial é EFEITO EXTERNO (dispara um envio de teste): não
 * retenta às cegas.
 */

const CHAVE_CANAIS = ["config-alertas", "canais"] as const;

/** Lista de canais no escopo `configurar_alertas` do usuário. */
export function useCanais(): UseQueryResult<Canal[]> {
  return useQuery({
    queryKey: CHAVE_CANAIS,
    queryFn: listarCanais,
  });
}

/** Cria canal; no sucesso invalida a lista de canais. */
export function useCriarCanal(): UseMutationResult<Canal, unknown, CanalIn> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: CanalIn) => criarCanal(corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS }),
  });
}

/** Argumento de atualização de canal: id + campos parciais. */
interface EditarCanalArgs {
  id: string;
  corpo: CanalPatch;
}

/** Atualiza canal; no sucesso invalida a lista de canais. */
export function useEditarCanal(): UseMutationResult<
  Canal,
  unknown,
  EditarCanalArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: EditarCanalArgs) => atualizarCanal(id, corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CHAVE_CANAIS }),
  });
}

/**
 * Dispara o teste de um canal (efeito externo). NÃO invalida a lista (testar não
 * muda o cadastro). O resultado (`ok`/`erro`) é consumido pelo `BotaoTestar`.
 */
export function useTestarCanal(): UseMutationResult<
  TesteCanal,
  unknown,
  string
> {
  return useMutation({
    mutationFn: (id: string) => testarCanal(id),
  });
}

/* ─────────────────────────────── Regras (FE-6b) ─────────────────────────────── */

const CHAVE_REGRAS = ["config-alertas", "regras"] as const;

/** Lista de regras no escopo `configurar_alertas` do usuário. */
export function useRegras(): UseQueryResult<Regra[]> {
  return useQuery({
    queryKey: CHAVE_REGRAS,
    queryFn: listarRegras,
  });
}

/** Cria regra; no sucesso invalida a lista de regras. */
export function useCriarRegra(): UseMutationResult<Regra, unknown, RegraIn> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (corpo: RegraIn) => criarRegra(corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CHAVE_REGRAS }),
  });
}

/** Argumento de atualização de regra: id + campos parciais (sem `empresa_id`). */
interface EditarRegraArgs {
  id: string;
  corpo: RegraPatch;
}

/** Atualiza regra; no sucesso invalida a lista de regras. */
export function useEditarRegra(): UseMutationResult<
  Regra,
  unknown,
  EditarRegraArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: EditarRegraArgs) => atualizarRegra(id, corpo),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: CHAVE_REGRAS }),
  });
}
